import {useMutation, useQuery} from '@apollo/client';
import {Box, Button, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Panel} from '../../components/Panel/Panel';
import {OneViewModelTypesEnum} from '../../schema-types/globalTypes';
import {
  GetNativeIdFromCuro,
  GetNativeIdFromCuroVariables,
} from '../../schema-types/GetNativeIdFromCuro';
import {CREATE_REVIEW} from '../../components/AccountOverview/accountOverviewQueries';
import {CreateReview, CreateReviewVariables} from '../../schema-types/CreateReview';
import {GET_NATIVE_ID_FROM_CURO} from './TestHomepage';
import {useQueryParams} from '../../hooks/useQueryParams';
import {handoverStepRoutes} from '../HandoverSummaryPage/HandoverStepTypes';
import validateUUID from '../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

export const TestNewTestingReviewPage: React.FC = () => {
  const {accountCuroId} = useParams();
  const params = useQueryParams();
  const [reviewType, setReviewType] = useState<'ad-hoc' | 'Annual' | null>(null);
  const curoId = validateUUID(accountCuroId?.replace(/&.+$/, ''));
  const {data} = useQuery<GetNativeIdFromCuro, GetNativeIdFromCuroVariables>(
    GET_NATIVE_ID_FROM_CURO,
    {
      variables: {
        curoId: curoId!,
        modelName: OneViewModelTypesEnum.ACCOUNT,
      },
      skip: !curoId,
    }
  );
  useEffect(() => {
    // query params always works async so need to set state first
    setReviewType((params.get('reviewType') as 'ad-hoc' | 'Annual') || 'ad-hoc');
  }, [params]);

  const {loading, reviewId, nativeAccountId} = useNewTestReview(
    data?.nativeId || null,
    reviewType
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (reviewId) {
      const supplement = params.get('goTo') ? `/${params.get('goTo')}` : '';
      if (supplement) {
        navigate({
          pathname: `/account/${data?.nativeId}/review-journey/${reviewId}${supplement}`,
        });
      }
    }
  }, [reviewId, data, params]);
  const {createRecommendUrl} = useRequirementsDentistOrBAU();

  return (
    <div>
      <Panel title="Test requirements / recommendations">
        {loading ? <Box>Setting up test</Box> : null}
        {!loading && !reviewId ? <div>Did not create new review</div> : null}
        <Typography>For testing purposes</Typography>
        {accountCuroId ? (
          <>
            Curo id: <Box id="account-id">{accountCuroId}</Box>
          </>
        ) : null}
        {nativeAccountId ? (
          <>
            Account id: <Box id="account-id">{nativeAccountId}</Box>
          </>
        ) : null}
        {reviewId ? (
          <>
            Review id: <Box id="review-id">{reviewId}</Box>
          </>
        ) : null}
        {reviewId ? (
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => {
                navigate({
                  pathname: `/account/${nativeAccountId}/review-journey/${reviewId}`,
                });
              }}
            >
              Go to review
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                navigate({
                  pathname: `/account/${nativeAccountId}/review-journey/${reviewId}/${handoverStepRoutes.meeting}`,
                });
              }}
            >
              Go to header (force)
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                navigate({
                  pathname: `/account/${nativeAccountId}/review-journey/${reviewId}/${handoverStepRoutes.requirements}`,
                });
              }}
            >
              View requirements
            </Button>

            <Button
              variant="contained"
              color="primary"
              size="large"
              data-test="Create Requirement"
              onClick={() => {
                navigate({
                  pathname: `/account/${nativeAccountId}/review-journey/${reviewId}/${createRecommendUrl}`,
                });
              }}
            >
              Create requirement
            </Button>
          </Box>
        ) : null}
      </Panel>
    </div>
  );
};
const useNewTestReview = (nativeAccountId: string | null, reviewType: string | null) => {
  const [loading, setLoading] = useState(true);
  // const {
  //   loading: accountDataLoading,
  //   data: testAccountData,
  //   error: accountDataError,
  // } = useQuery<AccountReviews, AccountReviewsVariables>(GET_ACCOUNT_REVIEWS, {
  //   variables: {
  //     accountId: nativeAccountId!,
  //     idType: 'id',
  //   },
  //   skip: !nativeAccountId,
  // });
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [createReview, {loading: createReviewLoading, data: createReviewData}] =
    useMutation<CreateReview, CreateReviewVariables>(CREATE_REVIEW);
  const navigate = useNavigate();
  useEffect(() => {
    if (!nativeAccountId || !reviewType) {
      return;
    }

    createReview({
      variables: {
        accountId: validateUUID(nativeAccountId)!,
        reviewType, // annual or ad-hoc
        name: `Test ${reviewType} review`,
        ignoreLimit: true,
      },
    })
      .then((createReviewResult) => {
        if (createReviewResult.data?.createReview?.review?.id) {
          setReviewId(createReviewResult.data?.createReview?.review?.id);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [nativeAccountId, reviewType]);

  return {loading, reviewId, nativeAccountId};
};
